// Insights: stage counts, the averages the forecast uses, what a new child is worth by age, and every assumption.

import React from "react";
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import type { CrmResult, LtvAssumptions } from "../../shared/crm";
import { STAGE_LABEL, STAGES, valueByStartingAge } from "../../shared/crm";
import { STAGE_COLOR, STAGE_HINT, Section, money } from "./ui";

const pct = (x: number) => `${Math.round(x * 100)}%`;

export const InsightsPanel: React.FC<{ result: CrmResult; assumptions: LtvAssumptions; onStage: (stage: string) => void }> = ({ result, assumptions, onStage }) => {
  const { population, stats, customers } = result;
  const ages = valueByStartingAge(population, assumptions);
  const children = customers.flatMap((c) => c.children);
  const top = [...customers].sort((x, y) => y.ltvPence - x.ltvPence).slice(0, 5);
  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
      <Section title="Families by stage">
        <Stack spacing={1}>
          {STAGES.map((stage) => (
            <Stack key={stage} direction="row" spacing={1} alignItems="center" sx={{ cursor: "pointer" }} onClick={() => onStage(stage)}>
              <Chip size="small" color={STAGE_COLOR[stage]} label={STAGE_LABEL[stage]} sx={{ minWidth: 100 }} />
              <Typography variant="body2" sx={{ width: 32, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{stats.byStage[stage]}</Typography>
              <Typography variant="caption" color="text.secondary">{STAGE_HINT[stage]}</Typography>
            </Stack>
          ))}
        </Stack>
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          {stats.bothServices} of {stats.customers} families use both Preschool and Holiday Club · {children.length} children
        </Typography>
      </Section>

      <Section title="Averages behind the forecast">
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Weekly preschool fee</TableCell>
              <TableCell align="right">{money(population.weeklyPence)}</TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">
                {population.fromData.weekly ? `from ${population.weeklyChildren} children's last term invoice` : `default (only ${population.weeklyChildren} children with term invoices)`}
              </Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Holiday Club spend a year</TableCell>
              <TableCell align="right">{money(population.holidayYearPence)}</TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">
                {population.fromData.holiday ? `average of ${population.holidayChildren} children who booked` : `default (only ${population.holidayChildren} children with bookings)`}
              </Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Preschool → Holiday Club take-up</TableCell>
              <TableCell align="right">{pct(population.takeUp)}</TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">
                {population.fromData.takeUp ? `of ${population.preschoolChildren} preschool children` : `default (only ${population.preschoolChildren} preschool children)`}
              </Typography></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Section>

      <Section title="What a new child is worth, by age">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Joins</TableCell>
              <TableCell align="right">Preschool</TableCell>
              <TableCell align="right">Holiday</TableCell>
              <TableCell align="right">Lifetime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ages.map((row) => (
              <TableRow key={row.label} title={row.detail}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.preschoolPence ? money(row.preschoolPence) : "—"}</TableCell>
                <TableCell align="right">{money(row.holidayPence)}</TableCell>
                <TableCell align="right"><b>{money(row.totalPence)}</b></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          School at about {assumptions.typicalSchoolStartAge}, Holiday Club until about {assumptions.typicalHolidayEndAge};
          preschool children take up Holiday Club at the rate above. Hover a row for the sums.
        </Typography>
      </Section>

      <Section title="Most valuable families">
        <Table size="small">
          <TableBody>
            {top.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell align="right">{money(c.realised.invoicedPence)}</TableCell>
                <TableCell align="right"><b>{money(c.ltvPence)}</b></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Paper variant="outlined" sx={{ p: 2, gridColumn: { md: "1 / -1" } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Assumptions</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { typography: "body2", mb: 0.5 } }}>
          <li>Realised value = invoices issued, cancelled ones left out (paid and outstanding shown separately). Holiday invoices for siblings are split by the value of each child's days.</li>
          <li>Children start school in the September after they turn 4 (1 September cut-off). Preschool forecast = remaining terms
            ({assumptions.termWeeks.autumn}/{assumptions.termWeeks.spring}/{assumptions.termWeeks.summer} weeks autumn/spring/summer) × the child's weekly fee
            (last term invoice ÷ its weeks; otherwise enrolment × {assumptions.hoursPerSession} h at {money(assumptions.privateRatePence)}/h private,
            funded hours at £{(assumptions.consumablePence / 100).toFixed(2)}/h consumables). The current term counts only if it is not invoiced yet.
            A child whose date of birth is unknown gets {assumptions.unknownDobPreschoolTerms} terms.</li>
          <li>Holiday Club from age {assumptions.holidayMinAge} until 31 August after Year 6 (about 11; the public site only says "3 and above").
            Yearly spend = the child's own (holiday invoiced ÷ years since first booking, at least one year), else the average × take-up.
            Age unknown: {assumptions.unknownAgeHolidayYears} more years.</li>
          <li>Forecast × retention by stage: {STAGES.map((s) => `${STAGE_LABEL[s]} ${pct(assumptions.retention[s])}`).join(", ")}.
            A stage set by hand also sets retention.</li>
          <li>Lapsed = no booking for {assumptions.lapseMonths} months. A registration without a child record counts as registered for {assumptions.registrationValidMonths} months
            (or until its start date), then as never started. Fees do not change when a child becomes entitled to funding; no inflation or discounting.</li>
        </Box>
      </Paper>
    </Box>
  );
};
